import { api } from '@/lib/api/client';
import type {
  AccountFunds,
  AccountSummary,
  ConditionalOrders,
  HoldingGuide,
  OrderPage,
  PnlCurvePoint,
} from './accountTypes';

/**
 * 계좌·보유·체결 조회 API. (12단계)
 *
 * 🔴 이 파일에는 조회만 있다
 *   주문을 만들거나 취소하는 함수를 여기 두지 않는다.
 *
 *   ✏️ 2026-09-20 에 CLAUDE.md 1.1 이 개정돼 주문이 **조건부 허용**이 됐다
 *     (전면 금지 → `trading/autotrade/` 한 곳에서만, 조건 여덟을 갖췄을 때).
 *     그래도 이 파일은 그대로다 — 주문 경로는 autotrade 모듈에 모은다.
 *     흩어지면 몇 달 뒤 "누가 어디서 주문을 내는가" 를 찾을 수 없다.
 *     자동매매 화면은 `autoTradeApi.ts` 가 따로 맡는다.
 *
 * 🔴 이 경로는 TRADING_ACCOUNT 메뉴 권한을 요구한다
 *   추천 화면(TRADING) 권한만 있으면 403 이 난다.
 *   토스 API 키가 하나뿐이라 이 화면에는 본인 계좌 잔고가 그대로 보인다.
 */

/**
 * 계좌 목록과 가장 최근 보유 스냅샷, 통화별 합계.
 *
 * 첫 동기화 전에는 전부 빈 상태로 온다. 오류가 아니다.
 */
export async function fetchAccountSummary(): Promise<AccountSummary> {
  return api.get<AccountSummary>('/trading/account');
}

/**
 * [지금 동기화] — 60초를 기다리지 않고 토스에서 다시 받아 요약을 돌려준다 (2026-10-16, docs/impl/150 1번).
 *
 * 🔴 POST 지만 토스에는 조회만 나간다. 우리 DB 스냅샷을 새로 쓰는 일이라 POST 다.
 *   10초 안에 다시 누르면 서버가 토스를 부르지 않고 지금 값만 돌려준다.
 */
export async function refreshAccount(): Promise<AccountSummary> {
  return api.post<AccountSummary>('/trading/account/refresh');
}

/**
 * 돈 카드 — 주문 가능 현금 · 수수료 (2026-10-16, docs/impl/150 2 · 3번).
 * 현금은 서버가 동기화 때 받아 둔 값이고, 수수료는 서버가 6시간마다 한 번 토스에서 받는다.
 */
export async function fetchAccountFunds(): Promise<AccountFunds> {
  return api.get<AccountFunds>('/trading/account/funds');
}

/**
 * 실계좌 누적 손익 곡선 (2026-10-16, docs/impl/150 7번) — 실현(매매일지) + 평가(보유 스냅샷) · 통화별.
 * 총자산이 아니다 — 입출금 기록이 없어 입금이 수익처럼 보이기 때문이다.
 */
export async function fetchPnlCurve(): Promise<{ points: PnlCurvePoint[] }> {
  return api.get<{ points: PnlCurvePoint[] }>('/trading/account/pnl-curve');
}

/**
 * 보유 종목 × 시스템 기준선 (2026-10-16, docs/impl/150 5번) — 서버가 우리 일봉 · 감시 행으로 계산한다(토스 호출 없음).
 * 추천이 아니다 — 종목 상세의 사다리 안내와 같은 값을 보유 표에 옮겨 보인다.
 */
export async function fetchHoldingGuides(): Promise<HoldingGuide[]> {
  return api.get<HoldingGuide[]>('/trading/account/holdings/guide');
}

/**
 * 걸어 둔 조건주문(진행 중) — 토스 앱에서 건 것도 온다 (2026-10-16, docs/impl/150 4번).
 * 🔴 조회만 — 걸기 · 고치기 · 취소는 여기 없다(주문은 autotrade 모듈 한 곳 · CLAUDE.md 1.1).
 */
export async function fetchConditionalOrders(): Promise<ConditionalOrders> {
  return api.get<ConditionalOrders>('/trading/account/conditional-orders');
}

/**
 * 주문·체결 이력. 최신순이다.
 *
 * 체결되지 않은 주문도 함께 온다. 취소한 주문도 그날의 기록이기 때문이다.
 */
export async function fetchOrders(page: number, size: number): Promise<OrderPage> {
  return api.get<OrderPage>('/trading/account/orders', { page, size });
}
